import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Divisórias de dia na thread — puro, testável sem tela.
 *
 * Tudo aqui trabalha no **fuso local do navegador**, de propósito. Comparar em
 * UTC deslocaria a virada do dia em 3 horas para o Brasil: uma mensagem das 22h
 * de terça cairia na quarta, e a thread mostraria "Ontem" no que o atendente
 * acabou de mandar. `getFullYear`/`getMonth`/`getDate` já são locais.
 */

/**
 * Aceita Date, Firestore Timestamp ou nada.
 *
 * Devolve `null` para qualquer coisa que não seja uma data utilizável —
 * inclusive `Invalid Date`, que é um Date de verdade e passaria despercebido
 * num `instanceof`. Mensagem sem data não quebra a thread nem gera divisória.
 */
export function paraData(valor) {
  const data = typeof valor?.toDate === 'function' ? valor.toDate() : valor;
  if (!(data instanceof Date) || Number.isNaN(data.getTime())) return null;
  return data;
}

/** Duas datas caem no mesmo dia do calendário local? `null` nunca casa. */
export function mesmoDia(a, b) {
  const x = paraData(a);
  const y = paraData(b);
  if (!x || !y) return false;
  return (
    x.getFullYear() === y.getFullYear()
    && x.getMonth() === y.getMonth()
    && x.getDate() === y.getDate()
  );
}

/** O dia anterior ao de `data`, preservando o horário. */
const ontemDe = (data) => {
  const d = new Date(data.getTime());
  d.setDate(d.getDate() - 1);
  return d;
};

/**
 * "Hoje", "Ontem" ou a data por extenso — o rótulo da divisória.
 *
 * `hoje` é parâmetro com padrão, e não `new Date()` lido lá dentro, para o teste
 * não depender do relógio da máquina.
 *
 * Por extenso ("15 de agosto de 2026") em vez de `dd/MM/yyyy`: a divisória é
 * lida de relance no meio da conversa, e por extenso não se confunde com as
 * horas que aparecem em cada bolha.
 *
 * O ano fica SEMPRE, mesmo no ano corrente. Numa thread de recepção que anda há
 * meses, "15 de agosto" sem ano dá a mesma marca para 2025 e 2026.
 */
export function rotuloDoDia(data, hoje = new Date()) {
  const d = paraData(data);
  if (!d) return null;

  const referencia = paraData(hoje) ?? new Date();
  if (mesmoDia(d, referencia)) return 'Hoje';
  if (mesmoDia(d, ontemDe(referencia))) return 'Ontem';

  try {
    return format(d, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    // Reserva pedida no enunciado: se o locale falhar por qualquer motivo, uma
    // data em números é melhor que uma divisória vazia.
    return format(d, 'dd/MM/yyyy');
  }
}

/**
 * Onde entram as divisórias numa lista já ordenada por data crescente.
 *
 * Devolve a lista intercalada — `{ tipo: 'dia' }` e `{ tipo: 'mensagem' }` —
 * para o componente só desenhar, sem carregar a regra de quando quebrar.
 *
 * Regra: divisória antes da PRIMEIRA mensagem com data válida, e sempre que o
 * dia muda em relação à última data vista. Mensagem sem data válida entra na
 * lista normalmente, mas **não move o dia de referência**: um documento com
 * `enviadaEm` nulo (mensagem otimista, ainda sem `serverTimestamp`) não pode
 * abrir uma divisória falsa no meio da conversa.
 */
export function comDivisorias(mensagens, hoje = new Date()) {
  const saida = [];
  let ultima = null;

  for (const mensagem of mensagens ?? []) {
    const data = paraData(mensagem?.enviadaEm);

    if (data && !mesmoDia(data, ultima)) {
      saida.push({ tipo: 'dia', chave: `dia-${mensagem.id}`, rotulo: rotuloDoDia(data, hoje) });
      ultima = data;
    }

    saida.push({ tipo: 'mensagem', chave: mensagem.id, mensagem });
  }

  return saida;
}
